import { BLOG } from '../../utils/constants.js';
import { storefrontFetch } from '../../utils/storefrontFetch.js';
import getLocalePrefix from '../../lib/getLocalePrefix.js';

const TAG_PREFIX = BLOG.TAG_PREFIX;
const MAX_FIRST = 250;
const PAD_WIDTH = 2;
const YEAR_SLICE = -2;
const LOADING_HTML = '<div class="col-span-3 flex justify-center py-10"><div class="h-8 w-8 animate-spin rounded-full border-2 border-current border-t-transparent opacity-50"></div></div>';
const NO_IMAGE_HTML = '<div class="relative w-full"><div class="rounded-[10px] aspect-[430/220] bg-gray-100 w-full"></div></div>';

const ARTICLES_QUERY = `
  query BlogArticles(
    $handle: String!
    $first: Int!
    $after: String
    $sortKey: ArticleSortKeys!
    $reverse: Boolean!
    $query: String
    $language: LanguageCode
  ) @inContext(language: $language) {
    blog(handle: $handle) {
      articles(
        first: $first
        after: $after
        sortKey: $sortKey
        reverse: $reverse
        query: $query
      ) {
        pageInfo { hasNextPage endCursor }
        nodes {
          title
          handle
          publishedAt
          onlineStoreUrl
          tags
          image {
            url(transform: { maxWidth: 860, maxHeight: 440, crop: CENTER })
            altText
          }
          blog { handle }
        }
      }
    }
  }
`;

// The Storefront API uses cursor-based pagination — there is no "skip to page N" offset.
// When the user lands on page N via a standard Shopify URL (?page=N), we must first walk
// forward through the article list to obtain the cursor that sits at the end of page N-1,
// then use that cursor as the `after` argument to fetch page N's articles.
// CURSOR_QUERY fetches only cursor positions (no article data) to keep those traversal
// requests lightweight. Resolved cursors are cached in sessionStorage so repeated
// navigation within a session avoids redundant API calls.
const CURSOR_QUERY = `
  query GetCursors(
    $handle: String!
    $first: Int!
    $after: String
    $sortKey: ArticleSortKeys!
    $reverse: Boolean!
    $query: String
    $language: LanguageCode
  ) @inContext(language: $language) {
    blog(handle: $handle) {
      articles(
        first: $first
        after: $after
        sortKey: $sortKey
        reverse: $reverse
        query: $query
      ) {
        edges { cursor }
        pageInfo { hasNextPage }
      }
    }
  }
`;

function reverseForKey(sortKey) {
  return ['PUBLISHED_AT', 'UPDATED_AT', 'ID'].includes(sortKey);
}

function formatDate(iso) {
  const d = new Date(iso);
  const mm = String(d.getUTCMonth() + 1).padStart(PAD_WIDTH, '0');
  const dd = String(d.getUTCDate()).padStart(PAD_WIDTH, '0');
  const yy = String(d.getUTCFullYear()).slice(YEAR_SLICE);
  return `${mm}.${dd}.${yy}`;
}

function buildTagChips(tags) {
  const cats = tags.filter(t => t.startsWith(TAG_PREFIX));
  if (!cats.length) return '';
  const chips = cats.map(tag => {
    const name = tag.slice(TAG_PREFIX.length)
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return `<span class="text-dark-grey-2 text-xxs flex w-fit py-1 px-2.5 text-center border-dark-grey border-1 rounded-[100px]">${name}</span>`;
  }).join('');
  return `<div class="flex gap-2">${chips}</div>`;
}

function buildArticleCard(article, readMoreLabel) {
  // Build the URL relative to the active storefront locale. The Storefront API's
  // onlineStoreUrl returns the default-locale (English /en/) URL regardless of the
  // current language, so using it here would switch the visitor out of their locale.
  const url = `${getLocalePrefix()}/blogs/${article.blog.handle}/${article.handle}`;
  const escapedTitle = article.title.replace(/"/g, '&quot;');
  // Mirror the Liquid card's aria-label (blogs.article.read_more_title, e.g. "Read more: {{ title }}").
  const ariaLabel = readMoreLabel ? readMoreLabel.replace('{{ title }}', escapedTitle) : escapedTitle;
  const imgAlt = (article.image?.altText || article.title).replace(/"/g, '&quot;');
  const imgHtml = article.image
    ? `<div class="relative w-full"><img src="${article.image.url}" alt="${imgAlt}" class="rounded-[10px] aspect-[430/220] object-cover w-full" loading="lazy" width="430" height="220" /></div>`
    : NO_IMAGE_HTML;

  return `<a class="flex flex-col gap-4" href="${url}" aria-label="${ariaLabel}">
    ${imgHtml}
    <div class="flex flex-col gap-2">
      ${buildTagChips(article.tags)}
      <h3 class="text-dark-grey-2">${article.title}</h3>
      <p class="text-xs font-medium text-dark-grey-2">${formatDate(article.publishedAt)}</p>
    </div>
  </a>`;
}


export default function initBlogSort() {
  const container = document.querySelector('[data-blog-sort]');
  if (!container) return;

  const grid = container.querySelector('[data-blog-articles-grid]');
  const noResults = container.querySelector('[data-blog-no-results]');
  const fallback = container.querySelector('[data-blog-articles-fallback]');
  if (!grid) return;

  const config = {
    blogHandle: container.dataset.blogHandle,
    sortKey: container.dataset.sortKey || 'PUBLISHED_AT',
    perPage: parseInt(container.dataset.perPage, 10),
    currentPage: parseInt(container.dataset.currentPage, 10),
    shopDomain: container.dataset.shopDomain,
    currentTag: container.dataset.currentTag || null,
    readMoreLabel: container.dataset.readMoreLabel || '',
    language: container.dataset.language || null,
  };

  const reverse = reverseForKey(config.sortKey);
  const tagQuery = config.currentTag ? `tag:${config.currentTag}` : null;
  const storageKey = `sr-blog-cursors__${config.blogHandle}__${config.sortKey}__${config.currentTag || 'all'}`;

  function getCachedCursors() {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || '{}'); }
    catch { return {}; }
  }

  function saveCachedCursors(cursors) {
    try { sessionStorage.setItem(storageKey, JSON.stringify(cursors)); }
    catch { /* sessionStorage unavailable */ }
  }

  async function resolveCursor(targetPage) {
    if (targetPage <= 1) return null;

    const cached = getCachedCursors();
    if (cached[targetPage - 1]) return cached[targetPage - 1];

    let fromPage = 0;
    let fromCursor = null;
    for (let p = targetPage - 1; p >= 1; p--) {
      if (cached[p]) { fromPage = p; fromCursor = cached[p]; break; }
    }

    let cursor = fromCursor;
    let page = fromPage;

    while (page < targetPage - 1) {
      const pagesNeeded = targetPage - 1 - page;
      const toFetch = Math.min(pagesNeeded * config.perPage, MAX_FIRST);
      const pagesInBatch = Math.floor(toFetch / config.perPage);

      const data = await storefrontFetch(config.shopDomain, CURSOR_QUERY, {
        handle: config.blogHandle,
        first: toFetch,
        after: cursor || null,
        sortKey: config.sortKey,
        reverse,
        query: tagQuery,
        language: config.language,
      });

      const { edges } = data.blog.articles;
      for (let i = 0; i < pagesInBatch; i++) {
        const edgeIdx = (i + 1) * config.perPage - 1;
        if (edges[edgeIdx]) cached[page + i + 1] = edges[edgeIdx].cursor;
      }

      cursor = edges[edges.length - 1]?.cursor ?? cursor;
      page += pagesInBatch;
      if (!data.blog.articles.pageInfo.hasNextPage) break;
    }

    saveCachedCursors(cached);
    return cached[targetPage - 1] ?? null;
  }

  async function loadArticles() {
    grid.innerHTML = LOADING_HTML;
    if (noResults) noResults.hidden = true;

    try {
      const cursor = await resolveCursor(config.currentPage);
      const data = await storefrontFetch(config.shopDomain, ARTICLES_QUERY, {
        handle: config.blogHandle,
        first: config.perPage,
        after: cursor ?? null,
        sortKey: config.sortKey,
        reverse,
        query: tagQuery,
        language: config.language,
      });

      const { nodes, pageInfo } = data.blog.articles;

      if (pageInfo.hasNextPage && pageInfo.endCursor) {
        const cached = getCachedCursors();
        cached[config.currentPage] = pageInfo.endCursor;
        saveCachedCursors(cached);
      }

      if (!nodes.length) {
        grid.innerHTML = '';
        if (noResults) noResults.hidden = false;
        return;
      }

      grid.innerHTML = nodes.map(article => buildArticleCard(article, config.readMoreLabel)).join('');
    } catch (err) {
      console.error('[Blog sort]', err);
      grid.innerHTML = '';
      if (fallback) {
        fallback.hidden = false;
        fallback.classList.remove('hidden');
      }
    }
  }

  loadArticles();
}
